import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';

// Indian states and their capitals
const INDIAN_STATES = {
  "Andhra Pradesh": "Amaravati",
  "Arunachal Pradesh": "Itanagar",
  "Assam": "Dispur",
  "Bihar": "Patna",
  "Chhattisgarh": "Raipur",
  "Goa": "Panaji",
  "Gujarat": "Gandhinagar",
  "Haryana": "Chandigarh",
  "Himachal Pradesh": "Shimla",
  "Jharkhand": "Ranchi",
  "Karnataka": "Bengaluru",
  "Kerala": "Thiruvananthapuram",
  "Madhya Pradesh": "Bhopal",
  "Maharashtra": "Mumbai",
  "Manipur": "Imphal",
  "Meghalaya": "Shillong",
  "Mizoram": "Aizawl",
  "Nagaland": "Kohima",
  "Odisha": "Bhubaneswar",
  "Punjab": "Chandigarh",
  "Rajasthan": "Jaipur",
  "Sikkim": "Gangtok",
  "Tamil Nadu": "Chennai",
  "Telangana": "Hyderabad",
  "Tripura": "Agartala",
  "Uttar Pradesh": "Lucknow",
  "Uttarakhand": "Dehradun",
  "West Bengal": "Kolkata",
  "Andaman and Nicobar Islands": "Port Blair",
  "Chandigarh": "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu": "Daman",
  "Delhi": "New Delhi",
  "Jammu and Kashmir": "Srinagar",
  "Ladakh": "Leh",
  "Lakshadweep": "Kavaratti",
  "Puducherry": "Puducherry"
};

const CompleteProfilePage = ({ user, setUser }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [sameAsPhone, setSameAsPhone] = useState(false);
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Pre-fill data from OAuth if available
    if (user) {
      console.log('[Profile] User data available:', user.email);
      if (user.name) {
        const nameParts = user.name.trim().split(' ');
        setFirstName(nameParts[0] || '');
        setLastName(nameParts.slice(1).join(' ') || '');
      }
      if (user.first_name) setFirstName(user.first_name);
      if (user.last_name) setLastName(user.last_name);
      if (user.phone) setPhone(user.phone.replace(user.country_code || '', ''));
      if (user.country_code) setCountryCode(user.country_code);
    } else {
      console.log('[Profile] No user data available - may need to login');
    }
  }, [user]);

  const handleSameAsPhoneChange = (e) => {
    const checked = e.target.checked;
    setSameAsPhone(checked);
    if (checked) {
      setWhatsappNumber(phone);
    } else {
      setWhatsappNumber('');
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    if (sameAsPhone) {
      setWhatsappNumber(value);
    }
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setState(selectedState);
    if (selectedState && INDIAN_STATES[selectedState]) {
      setCity(INDIAN_STATES[selectedState]);
    } else {
      setCity('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    console.log('[Profile] Submitting profile completion...');
    console.log('[Profile] User state:', user ? user.email : 'No user');
    console.log('[Profile] Cookies:', document.cookie ? 'Present' : 'Empty');
    
    try {
      const response = await api.post('/auth/complete-profile', {
        first_name: firstName,
        last_name: lastName,
        phone: `${countryCode}${phone}`,
        country_code: countryCode,
        whatsapp_number: whatsappNumber ? `${countryCode}${whatsappNumber}` : null,
        state,
        city
      });
      
      console.log('[Profile] Success:', response.data);
      setUser(response.data.user);
      toast.success('Profile completed successfully!');
      navigate('/member-dashboard');
    } catch (error) {
      console.error('[Profile] Error:', error);
      console.error('[Profile] Error response:', error.response?.data);
      console.error('[Profile] Error status:', error.response?.status);
      const errorMsg = error.response?.data?.detail || 'Failed to complete profile';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6 py-8">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_haute-assets/artifacts/jb59wbkx_THC%20Final%20Logo%20%28Cutout%202%29.png" 
            alt="The Haute Circle" 
            className="h-40 mx-auto mb-6" 
          />
          <h1 className="text-3xl font-bold mb-2">Complete Your Profile</h1>
          <p className="text-gray-600 text-sm">We need a few more details to complete your registration</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            {(!user?.first_name || !user?.last_name) && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">First Name*</label>
                  <Input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Last Name*</label>
                  <Input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    placeholder="Enter last name"
                  />
                </div>
              </div>
            )}

            {/* Phone Number */}
            {!user?.phone && (
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number*</label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="+91">+91</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+971">+971</option>
                    <option value="+65">+65</option>
                  </select>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    required
                    placeholder="Enter phone number"
                    className="flex-1"
                  />
                </div>
              </div>
            )}

            {/* WhatsApp Number */}
            <div>
              <label className="block text-sm font-medium mb-2">WhatsApp Number</label>
              <Input
                type="tel"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="Enter WhatsApp number"
                disabled={sameAsPhone}
              />
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="sameAsPhone"
                  checked={sameAsPhone}
                  onChange={handleSameAsPhoneChange}
                  className="w-4 h-4 text-gold border-gray-300 rounded focus:ring-gold"
                />
                <label htmlFor="sameAsPhone" className="ml-2 text-sm text-gray-600">
                  Same as phone number
                </label>
              </div>
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium mb-2">State*</label>
              <select
                value={state}
                onChange={handleStateChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Select State</option>
                {Object.keys(INDIAN_STATES).map((stateName) => (
                  <option key={stateName} value={stateName}>{stateName}</option>
                ))}
              </select>
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium mb-2">Nearest City*</label>
              <Input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                placeholder="Capital city (auto-filled based on state)"
                readOnly
              />
            </div>

            <Button type="submit" className="w-full bg-black text-white hover:bg-black/90 mt-6" disabled={loading}>
              {loading ? 'Completing Profile...' : 'Complete Profile'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
